/**
 * Chat Store
 * Manages chat messages with per-tab persistence using Jotai
 */

import { atom } from 'jotai';
import { tabStorage } from '@/lib/tab-storage';
import type { ChatMessage } from '@/types/websocket.types';

// ============================================================================
// Persisted Chat State
// ============================================================================

/**
 * What is kept of the thread: the turns someone authored, and nothing a run
 * answered with. Setting a conversation up is work — turns written, edited,
 * re-cast — and a reload must not cost it. The reply, its rules and its timeline
 * are the run's output, which is read once and then asked for again; keeping
 * them would restore an answer to a question the reloaded page is no longer
 * asking.
 */
const keepSetupOnly = (messages: ChatMessage[]): ChatMessage[] => messages.filter((m) => !m.fromRun);

const THREAD_KEY = 'chat-messages-kim-ai';

/**
 * Per tab, because a run belongs to the tab that started it. Dropping only the
 * cross-tab subscription was not enough — both tabs still wrote one key, so
 * whichever mounted last rendered the other's thread.
 */
const setupStorage = tabStorage<ChatMessage[]>();

/**
 * The thread, for as long as the page lives. Storage is read once, as the page
 * loads: `atomWithStorage` reads it again on every mount and, since storage holds
 * only the setup, would drop the run someone left on screen.
 */
const threadAtom = atom<ChatMessage[]>(setupStorage.getItem(THREAD_KEY, []));

export const chatMessagesAtom = atom(
  (get) => get(threadAtom),
  (_get, set, messages: ChatMessage[]) => {
    set(threadAtom, messages);
    setupStorage.setItem(THREAD_KEY, keepSetupOnly(messages));
  }
);

// ============================================================================
// Derived Atoms
// ============================================================================

/**
 * Check if there are any messages
 */
export const hasMessagesAtom = atom((get) => get(chatMessagesAtom).length > 0);

/**
 * Get the last message
 */
export const lastMessageAtom = atom((get) => {
  const messages = get(chatMessagesAtom);
  return messages.length > 0 ? messages[messages.length - 1] : null;
});

/**
 * Get message count
 */
export const messageCountAtom = atom((get) => get(chatMessagesAtom).length);

// ============================================================================
// Action Atoms
// ============================================================================

/**
 * Add a new message to the chat
 */
export const addMessageAtom = atom(null, (get, set, message: ChatMessage) => {
  const messages = get(chatMessagesAtom);
  set(chatMessagesAtom, [...messages, message]);
});

/**
 * Set all messages (replaces existing)
 */
export const setMessagesAtom = atom(null, (_get, set, messages: ChatMessage[]) => {
  set(chatMessagesAtom, messages);
});

/**
 * Clear all messages from chat
 */
export const clearMessagesAtom = atom(null, (_get, set) => {
  set(chatMessagesAtom, []);
});

/**
 * Update a specific message by ID
 */
export const updateMessageAtom = atom(null, (get, set, { id, updates }: { id: string; updates: Partial<ChatMessage> }) => {
  const messages = get(chatMessagesAtom);
  const updatedMessages = messages.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg));
  set(chatMessagesAtom, updatedMessages);
});

/**
 * Remove a message by ID
 */
export const removeMessageAtom = atom(null, (get, set, id: string) => {
  const messages = get(chatMessagesAtom);
  set(
    chatMessagesAtom,
    messages.filter((msg) => msg.id !== id)
  );
});
